import { useEffect, useRef, useState } from "react";
import TalentClient, { TalentErrors } from '../apiClients/TalentClient';
import { Talent } from "../models/dtos";
import { useErrorDisplay } from "../components/ErrorDisplay"
import BusySpinner from "../components/BusySpinner";
import { Paper } from "@mui/material";
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { IsEmpty, IsNotEmpty, TalentFullName } from "../Helpers";
import { NameIdListItem } from "../models/custom-models";
import { useAccessInfo } from "../contexts/AccessTokenProvider";

export interface TalentAutocompleteProps {
    onUserSelect?: (TalentId: string) => (void | Promise<void>);
    talentId?: string;
    placeholderText?: string;
    readOnly?: boolean;
}

const TalentAutocompleteControl = ({ talentId, placeholderText, onUserSelect, readOnly = false }: TalentAutocompleteProps) => {

    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const access = useAccessInfo();

    const [talents, setTalents] = useState<NameIdListItem[]>([]);
    const [selectedTalent, setSelectedTalent] = useState<NameIdListItem | null>(null);

    const initialized = useRef<boolean>(false);

    const loadData = async () => {
        try {
            setBusy(true);
            let t = await new TalentClient().GetAll();
            const options = mapToOptions(t);
            setTalents(sortByTitle(options));

        } catch (error) {
            setError(error, TalentErrors.GetAll);
        }
        finally {
            setBusy(false);
        }
    };

    const mapToOptions = (talents: Talent[]): NameIdListItem[] => {
        const displayFirstNameThenLast = access.accessInfo?.selectedOrganizationSettings?.DisplayFirstNameThenLast ?? true;
        var options = talents.map((t) => (
            { Id: t.Id, Name: TalentFullName(t, displayFirstNameThenLast), IsSelected: false } as NameIdListItem
        ));

        return options;
    }

    useEffect(() => {
        if (initialized.current)
            return;

        initialized.current = true;
        loadData();
    });

    useEffect(() => {
        if (IsNotEmpty(talentId)) {
            let selectTalent = talents.find(x => x.Id === talentId);
            setSelectedTalent(selectTalent);
        }
    }, [talentId, talents]);

    const sortByTitle = (list: NameIdListItem[]) =>
        list.sort((a, b) => (a?.Name ?? '').localeCompare(b?.Name ?? ''));

    const handleOnChange = (e, v) => {
        if (readOnly) return;
        if(IsNotEmpty(v)){
            onUserSelect(v.Id);
        }
        else{
            setSelectedTalent(v);
        }
    }

    return (
        <>
            <ErrorDisplay />
            <BusySpinner component={Paper} busy={busy} >
                <Autocomplete
                    autoComplete={true}
                    autoSelect={true}
                    id="talent-autocomplete"
                    options={talents}
                    getOptionLabel={(option) => option.Name}
                    value={selectedTalent}
                    sx={{ width: '100%' }}
                    onChange={handleOnChange}
                    readOnly={readOnly}
                    disableClearable={readOnly}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={(placeholderText) ? placeholderText : "Talent"}
                            variant="outlined"
                            inputProps={{ ...params.inputProps, readOnly }}
                        />
                    )}
                />
            </BusySpinner>
        </>
    )

};


export default TalentAutocompleteControl;
